import { FC } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { NumericFormat } from "react-number-format";
import type { MinMaxBand } from "@/lib/scoring/types";

/** Formats a min/max numeric range as a human-readable string. */
export function getHumanMinMax(
  min?: number | null,
  max?: number | null,
  prefix = ""
): string {
  const fmt = (n: number) => `${prefix}${n.toLocaleString("en-US")}`;
  const hasMin = min != null;
  const hasMax = max != null;
  if (!hasMin && !hasMax) return "—";
  if (!hasMin) return `less than ${fmt(max!)}`;
  if (!hasMax) return `greater than ${fmt(min!)}`;
  return `between ${fmt(min!)} and ${fmt(max!)}`;
}

function getBandError(band: MinMaxBand, others: MinMaxBand[]): string | null {
  if (!band.score || band.score < 0) {
    return "Points can't be empty";
  }
  const hasMin = band.min != null;
  const hasMax = band.max != null;
  if (!hasMin && !hasMax) {
    return "Range is empty";
  }
  if (hasMin && hasMax && band.min! > band.max!) {
    return "Minimum value should be less than max";
  }
  const aMin = band.min ?? -Infinity;
  const aMax = band.max ?? Infinity;
  const overlaps = others.some((o) => {
    const bMin = o.min ?? -Infinity;
    const bMax = o.max ?? Infinity;
    return aMin < bMax && bMin < aMax;
  });
  if (overlaps) {
    return "Intervals intersect";
  }
  return null;
}

export function hasMinMaxErrors(bands: MinMaxBand[]): boolean {
  return bands.some(
    (band, i) =>
      getBandError(
        band,
        bands.filter((_, j) => j !== i)
      ) !== null
  );
}

interface MinMaxEditorProps {
  bands: MinMaxBand[];
  onChange: (bands: MinMaxBand[]) => void;
  minLabel?: string;
  maxLabel?: string;
  thousandSeparator?: boolean;
  prefix?: string;
  minInputTestId?: string;
  maxInputTestId?: string;
}

export const MinMaxEditor: FC<MinMaxEditorProps> = ({
  bands,
  onChange,
  minLabel = "Min",
  maxLabel = "Max",
  thousandSeparator = false,
  prefix = "",
  minInputTestId,
  maxInputTestId,
}) => {
  const updateBand = (index: number, patch: Partial<MinMaxBand>) => {
    onChange(bands.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  const removeBand = (index: number) => {
    onChange(bands.filter((_, i) => i !== index));
  };

  const addBand = () => {
    onChange([...bands, { score: 0 }]);
  };

  return (
    <Box sx={{ px: 2 }}>
      {bands.map((band, i) => {
        const others = bands.filter((_, j) => j !== i);
        const isDirty =
          band.min !== undefined || band.max !== undefined || band.score !== 0;
        const error = isDirty ? getBandError(band, others) : null;
        return (
          <Box key={i} sx={{ mb: error ? 0.5 : 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr 72px auto",
                gap: 1,
                alignItems: "center",
              }}
            >
              <NumericFormat
                customInput={TextField}
                size="small"
                label={minLabel}
                value={band.min ?? ""}
                error={!!error && error !== "Points can't be empty"}
                onValueChange={(values) =>
                  updateBand(i, {
                    min:
                      values.value === "" || values.floatValue == null
                        ? undefined
                        : values.floatValue,
                  })
                }
                thousandSeparator={thousandSeparator}
                prefix={prefix}
                decimalScale={0}
                allowNegative={false}
                {...(minInputTestId
                  ? { inputProps: { "data-testid": minInputTestId } }
                  : {})}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                —
              </Typography>
              <NumericFormat
                customInput={TextField}
                size="small"
                label={maxLabel}
                value={band.max ?? ""}
                error={!!error && error !== "Points can't be empty"}
                onValueChange={(values) =>
                  updateBand(i, {
                    max:
                      values.value === "" || values.floatValue == null
                        ? undefined
                        : values.floatValue,
                  })
                }
                thousandSeparator={thousandSeparator}
                prefix={prefix}
                decimalScale={0}
                allowNegative={false}
                {...(maxInputTestId
                  ? { inputProps: { "data-testid": maxInputTestId } }
                  : {})}
              />
              <TextField
                size="small"
                type="number"
                label="Pts"
                value={band.score === 0 ? "" : band.score}
                error={error === "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.25, mb: 0.75 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600, mt: 0.5 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
